// src/components/WorldlandBackground/WorldlandBackground.js
import React, { useEffect, useRef } from 'react';
import './WorldlandBackground.css';

const WorldlandBackground = () => {
  const canvasRef = useRef(null);
  const cursorRingRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const cursorRing = cursorRingRef.current;
    
    let width = canvas.width = window.innerWidth;
    let height = canvas.height = window.innerHeight;
    
    let mouseX = width / 2;
    let mouseY = height / 2;
    let targetMouseX = mouseX;
    let targetMouseY = mouseY;
    
    // 세계 지도 좌표 (간소화된 버전)
    const worldMapData = {
      northAmerica: [
        {x: -0.65, y: 0.35}, {x: -0.5, y: 0.5}, {x: -0.35, y: 0.45},
        {x: -0.3, y: 0.3}, {x: -0.4, y: 0.2}, {x: -0.55, y: 0.15}, {x: -0.65, y: 0.35}
      ],
      southAmerica: [
        {x: -0.4, y: -0.05}, {x: -0.3, y: -0.2}, {x: -0.35, y: -0.5},
        {x: -0.45, y: -0.55}, {x: -0.5, y: -0.3}, {x: -0.4, y: -0.05}
      ],
      europe: [
        {x: -0.05, y: 0.42}, {x: 0.15, y: 0.48}, {x: 0.25, y: 0.4},
        {x: 0.15, y: 0.35}, {x: 0.05, y: 0.38}, {x: -0.05, y: 0.42}
      ],
      africa: [
        {x: 0.05, y: 0.25}, {x: 0.25, y: 0.15}, {x: 0.35, y: 0},
        {x: 0.35, y: -0.35}, {x: 0.15, y: -0.4}, {x: 0.05, y: -0.1}, {x: 0.05, y: 0.25}
      ],
      asia: [
        {x: 0.25, y: 0.5}, {x: 0.55, y: 0.55}, {x: 0.75, y: 0.45},
        {x: 0.8, y: 0.25}, {x: 0.65, y: 0.15}, {x: 0.45, y: 0.2},
        {x: 0.35, y: 0.35}, {x: 0.25, y: 0.5}
      ],
      australia: [
        {x: 0.65, y: -0.25}, {x: 0.78, y: -0.22}, {x: 0.82, y: -0.38},
        {x: 0.72, y: -0.48}, {x: 0.62, y: -0.42}, {x: 0.65, y: -0.25}
      ]
    };
    
    // 3D 지구
    class Globe {
      constructor() {
        this.x = width / 2;
        this.y = height / 2 + 50;
        this.radius = 200;
        this.rotation = 0;
        this.tilt = 0.15;
        this.cities = this.generateCities();
      }
      
      generateCities() {
        const cities = [];
        const cityLocations = [
          {x: -0.6, y: 0.35}, {x: -0.4, y: 0.3}, {x: -0.35, y: 0.45},
          {x: -0.4, y: -0.3}, {x: -0.35, y: -0.5},
          {x: 0.1, y: 0.4}, {x: 0.2, y: 0.38}, {x: -0.05, y: 0.42},
          {x: 0.15, y: 0.1}, {x: 0.25, y: -0.15}, {x: 0.15, y: -0.35},
          {x: 0.4, y: 0.35}, {x: 0.55, y: 0.45}, {x: 0.65, y: 0.3},
          {x: 0.7, y: 0.2}, {x: 0.75, y: -0.35}
        ];
        
        cityLocations.forEach(loc => {
          cities.push({
            x: loc.x,
            y: loc.y,
            pulsePhase: Math.random() * Math.PI * 2,
            size: Math.random() * 2 + 3
          });
        });
        
        return cities;
      }
      
      rotatePoint(x, y, z, rotation) {
        const cosR = Math.cos(rotation);
        const sinR = Math.sin(rotation);
        const x1 = x * cosR - z * sinR;
        const z1 = x * sinR + z * cosR;
        
        const cosT = Math.cos(this.tilt);
        const sinT = Math.sin(this.tilt);
        const y1 = y * cosT - z1 * sinT;
        const z2 = y * sinT + z1 * cosT;
        
        return {x: x1, y: y1, z: z2};
      }
      
      project3DTo2D(x, y, z) {
        const scale = 400 / (400 + z);
        return {
          x: this.x + x * scale,
          y: this.y + y * scale,
          scale: scale,
          depth: z
        };
      }
      
      update() {
        this.rotation += 0.002;
        const targetX = width / 2 + (mouseX - width / 2) * 0.03;
        const targetY = height / 2 + 50 + (mouseY - height / 2) * 0.03;
        this.x += (targetX - this.x) * 0.1;
        this.y += (targetY - this.y) * 0.1;
      }
      
      drawContinent(points, isVisible) {
        if (!isVisible) return;
        
        const projectedPoints = [];
        let allFront = true;
        
        points.forEach(point => {
          const sphereX = point.x * this.radius;
          const sphereY = point.y * this.radius;
          const sphereZ = Math.sqrt(Math.max(0, 
            this.radius * this.radius - sphereX * sphereX - sphereY * sphereY));
          
          const rotated = this.rotatePoint(sphereX, sphereY, sphereZ, this.rotation);
          
          if (rotated.z < -50) allFront = false;
          
          projectedPoints.push(this.project3DTo2D(rotated.x, rotated.y, rotated.z));
        });
        
        if (!allFront) return;
        
        // 대륙 채우기
        ctx.fillStyle = 'rgba(100, 150, 200, 0.15)';
        ctx.strokeStyle = 'rgba(100, 200, 255, 0.3)';
        ctx.lineWidth = 1;
        ctx.beginPath();
        projectedPoints.forEach((point, i) => {
          if (i === 0) ctx.moveTo(point.x, point.y);
          else ctx.lineTo(point.x, point.y);
        });
        ctx.closePath();
        ctx.fill();
        ctx.stroke();
      }
      
      draw() {
        // 외부 대기권 글로우
        const atmosphereGradient = ctx.createRadialGradient(
          this.x - this.radius * 0.2, this.y - this.radius * 0.2, this.radius * 0.5,
          this.x, this.y, this.radius * 1.4
        );
        atmosphereGradient.addColorStop(0, 'rgba(100, 180, 255, 0.15)');
        atmosphereGradient.addColorStop(0.4, 'rgba(100, 200, 255, 0.08)');
        atmosphereGradient.addColorStop(1, 'rgba(100, 200, 255, 0)');
        
        ctx.fillStyle = atmosphereGradient;
        ctx.beginPath();
        ctx.arc(this.x, this.y, this.radius * 1.4, 0, Math.PI * 2);
        ctx.fill();
        
        // 지구 본체
        const gradient = ctx.createRadialGradient(
          this.x - this.radius * 0.3, this.y - this.radius * 0.3, this.radius * 0.1,
          this.x, this.y, this.radius
        );
        gradient.addColorStop(0, '#3a4d6e');
        gradient.addColorStop(0.5, '#2a3d5e');
        gradient.addColorStop(1, '#1a2d4e');
        
        ctx.fillStyle = gradient;
        ctx.beginPath();
        ctx.arc(this.x, this.y, this.radius, 0, Math.PI * 2);
        ctx.fill();
        
        // 대륙 그리기
        Object.values(worldMapData).forEach(continent => {
          this.drawContinent(continent, true);
        });
        
        // 도시 반짝임 효과
        return this.cities.map(city => {
          const sphereX = city.x * this.radius;
          const sphereY = city.y * this.radius;
          const sphereZ = Math.sqrt(Math.max(0, 
            this.radius * this.radius - sphereX * sphereX - sphereY * sphereY));
          
          const rotated = this.rotatePoint(sphereX, sphereY, sphereZ, this.rotation);
          const projected = this.project3DTo2D(rotated.x, rotated.y, rotated.z);
          
          if (rotated.z > -50) {
            const pulse = Math.sin(city.pulsePhase + Date.now() * 0.002) * 0.5 + 0.5;
            const citySize = city.size * projected.scale * (0.7 + pulse * 0.3);
            
            // 도시 글로우
            const cityGlow = ctx.createRadialGradient(
              projected.x, projected.y, 0,
              projected.x, projected.y, citySize * 3
            );
            cityGlow.addColorStop(0, `rgba(255, 220, 100, ${pulse * 0.6})`);
            cityGlow.addColorStop(0.5, `rgba(255, 200, 100, ${pulse * 0.3})`);
            cityGlow.addColorStop(1, 'rgba(255, 200, 100, 0)');
            
            ctx.fillStyle = cityGlow;
            ctx.beginPath();
            ctx.arc(projected.x, projected.y, citySize * 3, 0, Math.PI * 2);
            ctx.fill();
            
            // 도시 본체
            ctx.fillStyle = `rgba(255, 230, 150, ${0.8 + pulse * 0.2})`;
            ctx.beginPath();
            ctx.arc(projected.x, projected.y, citySize, 0, Math.PI * 2);
            ctx.fill();
          }
          
          return {
            ...projected,
            visible: rotated.z > -50,
            pulsePhase: city.pulsePhase,
            size: city.size
          };
        });
      }
    }
    
    // 네트워크 노드 (사람들)
    class NetworkNode {
      constructor(cityPos) {
        if (cityPos) {
          this.x = cityPos.x;
          this.y = cityPos.y;
          this.isCity = true;
        } else {
          this.x = Math.random() * width;
          this.y = Math.random() * height;
          this.isCity = false;
        }
        
        this.vx = (Math.random() - 0.5) * 0.3;
        this.vy = (Math.random() - 0.5) * 0.3;
        this.size = this.isCity ? 4 : Math.random() * 3 + 2;
        this.pulsePhase = Math.random() * Math.PI * 2;
        this.pulseSpeed = Math.random() * 0.03 + 0.02;
        this.connections = [];
      }
      
      update() {
        if (!this.isCity) {
          this.x += this.vx;
          this.y += this.vy;
          
          // 마우스 영향
          const dx = this.x - mouseX;
          const dy = this.y - mouseY;
          const dist = Math.sqrt(dx * dx + dy * dy);
          
          if (dist < 150) {
            const force = (150 - dist) / 150;
            this.vx += (dx / dist) * force * 0.15;
            this.vy += (dy / dist) * force * 0.15;
          }
          
          this.vx *= 0.98;
          this.vy *= 0.98;
          
          if (this.x < 0 || this.x > width) this.vx *= -1;
          if (this.y < 0 || this.y > height) this.vy *= -1;
          
          this.x = Math.max(0, Math.min(width, this.x));
          this.y = Math.max(0, Math.min(height, this.y));
        }
        
        this.pulsePhase += this.pulseSpeed;
      }
      
      draw() {
        const pulse = Math.sin(this.pulsePhase) * 0.3 + 0.7;
        const currentSize = this.size * pulse;
        
        // 노드 글로우
        const glowGradient = ctx.createRadialGradient(
          this.x, this.y, 0,
          this.x, this.y, currentSize * 4
        );
        glowGradient.addColorStop(0, 'rgba(100, 200, 255, 0.8)');
        glowGradient.addColorStop(0.5, 'rgba(100, 200, 255, 0.3)');
        glowGradient.addColorStop(1, 'rgba(100, 200, 255, 0)');
        
        ctx.fillStyle = glowGradient;
        ctx.beginPath();
        ctx.arc(this.x, this.y, currentSize * 4, 0, Math.PI * 2);
        ctx.fill();
        
        // 노드 본체
        ctx.fillStyle = this.isCity ? 
          'rgba(150, 220, 255, 0.95)' : 
          'rgba(100, 200, 255, 0.9)';
        ctx.beginPath();
        ctx.arc(this.x, this.y, currentSize, 0, Math.PI * 2);
        ctx.fill();
        
        // 하이라이트
        ctx.fillStyle = 'rgba(255, 255, 255, 0.6)';
        ctx.beginPath();
        ctx.arc(this.x - currentSize * 0.3, this.y - currentSize * 0.3, 
          currentSize * 0.4, 0, Math.PI * 2);
        ctx.fill();
      }
    }
    
    // 초기화
    const globe = new Globe();
    const nodes = [];
    
    // 주변 네트워크 노드 생성
    for (let i = 0; i < 120; i++) {
      nodes.push(new NetworkNode());
    }
    
    // 연결 생성 함수
    function updateConnections() {
      nodes.forEach(node => {
        node.connections = [];
      });
      
      const maxDistance = 150;
      
      for (let i = 0; i < nodes.length; i++) {
        for (let j = i + 1; j < nodes.length; j++) {
          const dx = nodes[i].x - nodes[j].x;
          const dy = nodes[i].y - nodes[j].y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          
          if (dist < maxDistance) {
            nodes[i].connections.push({
              node: nodes[j],
              distance: dist,
              maxDistance: maxDistance
            });
          }
        }
      }
    }
    
    // 연결선 그리기
    function drawConnections() {
      nodes.forEach(node => {
        node.connections.forEach(conn => {
          const opacity = 1 - (conn.distance / conn.maxDistance);
          ctx.strokeStyle = `rgba(100, 200, 255, ${opacity * 0.2})`;
          ctx.lineWidth = 0.5;
          ctx.beginPath();
          ctx.moveTo(node.x, node.y);
          ctx.lineTo(conn.node.x, conn.node.y);
          ctx.stroke();
        });
      });
    }
    
    // 마우스 이벤트
    const handleMouseMove = (e) => {
      targetMouseX = e.clientX;
      targetMouseY = e.clientY;
      
      if (cursorRing) {
        cursorRing.style.left = (e.clientX - 30) + 'px';
        cursorRing.style.top = (e.clientY - 30) + 'px';
      }
    };
    
    const handleResize = () => {
      width = canvas.width = window.innerWidth;
      height = canvas.height = window.innerHeight;
    };
    
    canvas.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('resize', handleResize);
    
    let frameCount = 0;
    let animationId;
    
    // 애니메이션 루프
    function animate() {
      // 배경
      const bgGradient = ctx.createRadialGradient(
        width / 2, height / 2, 0,
        width / 2, height / 2, Math.max(width, height) * 0.8
      );
      bgGradient.addColorStop(0, '#3d4575');
      bgGradient.addColorStop(0.5, '#252b4a');
      bgGradient.addColorStop(1, '#151929');
      
      ctx.fillStyle = bgGradient;
      ctx.fillRect(0, 0, width, height);
      
      // 마우스 부드러운 이동
      mouseX += (targetMouseX - mouseX) * 0.08;
      mouseY += (targetMouseY - mouseY) * 0.08;
      
      // 지구 업데이트
      globe.update();
      const cityPositions = globe.draw();
      
      // 도시 노드 업데이트
      cityPositions.forEach((cityPos, i) => {
        if (cityPos.visible && i < 16) {
          if (!nodes[100 + i] || !nodes[100 + i].isCity) {
            nodes[100 + i] = new NetworkNode(cityPos);
          } else {
            nodes[100 + i].x = cityPos.x;
            nodes[100 + i].y = cityPos.y;
          }
        }
      });
      
      // 노드 업데이트
      nodes.forEach(node => node.update());
      
      // 연결 업데이트 (매 3프레임마다)
      if (frameCount % 3 === 0) {
        updateConnections();
      }
      
      // 연결선 그리기
      drawConnections();
      
      // 노드 그리기
      nodes.forEach(node => node.draw());
      
      frameCount++;
      animationId = requestAnimationFrame(animate);
    }
    
    animate();
    
    // Cleanup
    return () => {
      cancelAnimationFrame(animationId);
      canvas.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  return (
    <>
      <div className="cursor-ring" ref={cursorRingRef}></div>
      <canvas ref={canvasRef} className="worldland-canvas"></canvas>
    </>
  );
};

export default WorldlandBackground;